import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gauge, Fuel, Cog, CalendarDays, Zap, ArrowUpRight } from "lucide-react";
import { formatPrice, formatKm } from "@/lib/format";
import { assetUrl } from "@/lib/api";
import SmartImage from "@/components/site/SmartImage";
import { TID } from "@/constants/tids";

const badge = {
  nou: { label: "Disponibilă", cls: "bg-[#D4AF37] text-[#0a0a0a]" },
  rezervata: { label: "Rezervată", cls: "bg-white text-[#0a0a0a]" },
  vanduta: { label: "Vândută", cls: "bg-red-500/90 text-white" },
};

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="h-8 w-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.06] shrink-0">
        <Icon className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.15em] text-white/40 font-semibold">
          {label}
        </div>
        <div className="text-[13px] font-semibold text-white truncate">
          {value}
        </div>
      </div>
    </div>
  );
}

export default function CarCard({ car, index = 0 }) {
  const b = badge[car.status] || badge.nou;
  const img = car.images?.[0]
    ? assetUrl(car.images[0])
    : "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200";

  const engine = car.engine_size ? `${car.engine_size.toFixed(1)}L` : "—";
  const powerLine = car.power_hp
    ? `${engine} · ${car.power_hp} CP`
    : engine;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06 }}
      className="h-full"
    >
      <Link
        to={`/masini/${car.slug}`}
        data-testid={TID.carCard}
        className="group relative block h-full flex flex-col rounded-[22px] overflow-hidden bg-[#111111] border border-white/[0.06] hover:border-[#D4AF37]/40 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.9)] hover:shadow-[0_30px_70px_-15px_rgba(212,175,55,0.3),0_10px_40px_-10px_rgba(0,0,0,0.9)] hover:-translate-y-1.5 transition-all duration-500"
      >
        {/* Image */}
        <div className="relative aspect-[16/11] w-full overflow-hidden bg-black">
          <SmartImage
            src={img}
            context="card"
            alt={`${car.brand} ${car.model}`}
            className="object-cover w-full h-full transition-transform duration-[900ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

          <span
            className={`absolute top-4 left-4 text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${b.cls}`}
          >
            {b.label}
          </span>
          <div className="absolute top-4 right-4 text-[11px] font-bold text-white/90 border border-white/20 bg-black/50 backdrop-blur-md rounded-full px-3 py-1.5">
            {car.year}
          </div>

          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-1">
              {car.brand}
            </div>
            <h3 className="font-extrabold text-2xl sm:text-[26px] leading-[1.1] text-white line-clamp-1 tracking-tight">
              {car.model}
            </h3>
          </div>
        </div>

        {/* Gold price band — full width */}
        <div className="relative bg-[#D4AF37] py-4 px-5 flex items-baseline justify-between gap-3 overflow-hidden">
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background:
                "linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.4) 50%, transparent 80%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 2.4s linear infinite",
            }}
          />
          <div className="relative text-[#0a0a0a] font-extrabold text-[26px] sm:text-[30px] leading-none tracking-tight font-display">
            {formatPrice(car.price)}
          </div>
          <div className="relative inline-flex items-center gap-1 bg-[#0a0a0a] text-[#D4AF37] font-bold text-[11px] uppercase tracking-[0.18em] rounded-full px-3 py-1.5">
            Detalii
            <ArrowUpRight className="h-3.5 w-3.5" />
          </div>
        </div>

        {/* Specs grid */}
        <div className="p-5 flex-grow flex flex-col gap-3.5">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Spec icon={Zap} label="Motorizare" value={powerLine} />
            <Spec icon={Fuel} label="Combustibil" value={car.fuel} />
            <Spec icon={Cog} label="Cutie" value={car.transmission} />
            <Spec icon={CalendarDays} label="An" value={car.year} />
            <Spec icon={Gauge} label="Kilometri" value={formatKm(car.mileage)} />
            {car.body_type && (
              <Spec
                icon={ArrowUpRight}
                label="Caroserie"
                value={car.body_type}
              />
            )}
          </div>

          <div
            data-testid={TID.carCardCta}
            className="mt-1 pt-3.5 border-t border-white/[0.06] flex items-center justify-between text-[12px]"
          >
            <span className="text-white/45 font-medium">
              Verificată tehnic · Garanție
            </span>
            <span className="text-[#D4AF37] font-bold uppercase tracking-[0.16em] group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">
              Vezi mașina
              <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
